import { useCallback, useEffect, useRef, useState } from 'react';
import type { CreateJobRequest, Job, JobLikenessRating } from '@volt/shared';
import { useSession } from '../state/session.js';

const PAGE_SIZE = 24;

export interface JobsState {
  jobs: Job[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  generating: boolean;
  create: (body: CreateJobRequest) => Promise<Job>;
  reload: () => Promise<void>;
  loadMore: () => Promise<void>;
  toggleFavorite: (jobId: string, isFavorite: boolean) => Promise<void>;
  /** "Looks like me" / "does not look like me" on a character result. */
  rateLikeness: (jobId: string, rating: JobLikenessRating | null) => Promise<void>;
  removeJob: (jobId: string) => Promise<void>;
}

/**
 * Polls the first page of async generation jobs every 4s while `active`, and
 * exposes `loadMore` to fetch older pages for infinite scroll. Polling only
 * ever refreshes the "head" of the list (the most recent jobs), so it never
 * resets scroll position or discards jobs already paged into view.
 */
export function useJobs(active: boolean): JobsState {
  const { api } = useSession();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const mounted = useRef(true);
  const cursorRef = useRef<string | null>(null);
  const initializedRef = useRef(false);
  const deletedIdsRef = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const response = await api.listJobs({ limit: PAGE_SIZE });
      if (!mounted.current) return;
      const headIds = new Set(response.jobs.map((job) => job.id));
      const freshHead = response.jobs.filter((job) => !deletedIdsRef.current.has(job.id));
      setJobs((prev) => {
        const tail = prev.filter(
          (job) => !headIds.has(job.id) && !deletedIdsRef.current.has(job.id),
        );
        return [...freshHead, ...tail];
      });
      if (!initializedRef.current) {
        initializedRef.current = true;
        cursorRef.current = response.nextCursor;
        setHasMore(response.hasMore);
      }
    } catch {
      /* keep previous jobs */
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!active) return;
    void load();
    const interval = window.setInterval(() => void load(), 4000);
    return () => window.clearInterval(interval);
  }, [active, load]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !cursorRef.current) return;
    setLoadingMore(true);
    try {
      const response = await api.listJobs({ limit: PAGE_SIZE, cursor: cursorRef.current });
      if (mounted.current) {
        setJobs((prev) => {
          const existingIds = new Set(prev.map((job) => job.id));
          const additions = response.jobs.filter(
            (job) => !existingIds.has(job.id) && !deletedIdsRef.current.has(job.id),
          );
          return [...prev, ...additions];
        });
      }
      cursorRef.current = response.nextCursor;
      if (mounted.current) setHasMore(response.hasMore);
    } catch {
      /* keep previous cursor so the sentinel can retry */
    } finally {
      if (mounted.current) setLoadingMore(false);
    }
  }, [api, hasMore, loadingMore]);

  const create = useCallback(
    async (body: CreateJobRequest) => {
      setGenerating(true);
      try {
        const job = await api.createJob(body);
        if (mounted.current) setJobs((prev) => [job, ...prev]);
        return job;
      } finally {
        if (mounted.current) setGenerating(false);
      }
    },
    [api],
  );

  const toggleFavorite = useCallback(
    async (jobId: string, isFavorite: boolean) => {
      const updated = await api.toggleJobFavorite(jobId, isFavorite);
      if (mounted.current) {
        setJobs((prev) => prev.map((job) => (job.id === jobId ? updated : job)));
      }
    },
    [api],
  );

  const rateLikeness = useCallback(
    async (jobId: string, rating: JobLikenessRating | null) => {
      const updated = await api.rateJobLikeness(jobId, rating);
      if (mounted.current) {
        setJobs((prev) => prev.map((job) => (job.id === jobId ? updated : job)));
      }
    },
    [api],
  );

  const removeJob = useCallback(
    async (jobId: string) => {
      deletedIdsRef.current.add(jobId);
      let removed: Job | undefined;
      if (mounted.current) {
        setJobs((prev) => {
          removed = prev.find((job) => job.id === jobId);
          return prev.filter((job) => job.id !== jobId);
        });
      }
      try {
        await api.deleteJob(jobId);
      } catch (error) {
        deletedIdsRef.current.delete(jobId);
        if (mounted.current && removed) {
          const restored = removed;
          setJobs((prev) => (prev.some((job) => job.id === jobId) ? prev : [...prev, restored]));
        }
        throw error;
      }
    },
    [api],
  );

  return {
    jobs,
    loading,
    loadingMore,
    hasMore,
    generating,
    create,
    reload: load,
    loadMore,
    toggleFavorite,
    rateLikeness,
    removeJob,
  };
}
